import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Arrow } from "../../../../assets/icons";
import { PortalHeader } from "../../../../components/molecules/Header";
import { ModalOverlay, ModalT1 } from "../../../../components/organisms/Modal";
import { ConfirmationModal } from "../../../../components/organisms/Modal/confirmationModal";
import { useNotification } from "../../../../hooks/useNotification";
import { useDeleteBomMutation } from "../../../../store/apiSlice/productionApiSlice";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ListActionBurgerMenu } from "../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";
import {
  StyledPromoDescription as StyledBomDescription,
  StyledPromoTextContent as StyledBomTextContent,
  StyledPromoTitle as StyledBomTitle,
  StyledPromoTypeContainer as StyledBomTypeContainer,
  StyledPromoTypeItem as StyledBomTypeItem,
} from "../../../promo/layout/action/action.styles";
import { OrderlessManufacturingEntryModal } from "./components/orderlessManufacturingEntry";

const rootModals = document.getElementById("root-modals");

const BomAction = ({ selectedOrderId, setSelectedOrder, selectedOrderFromListview }) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const [isOrderlessEntryOpen, setIsOrderlessEntryOpen] = useState(false);
  const [deleteBom, { isLoading: isDeleteLoading } = {}] = useDeleteBomMutation();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const isDisableOrderlessEntry = useMemo(() => {
    const selected = selectedOrderFromListview?.[0];
    return (
      selectedOrderId.length !== 1 ||
      selected?.status?.id !== "active" ||
      selected?.type?.id !== "standard"
    );
  }, [selectedOrderId, selectedOrderFromListview]);

  const handleUpdate = () => {
    if (selectedOrderId.length !== 1) {
      notify({ type: "warning", message: "Please select exactly one BOM to update." });
      return;
    }
    navigate(`/bom/manage/update/${selectedOrderId[0]}`);
  };

  const handleDelete = async () => {
    try {
      await deleteBom(selectedOrderId[0]).unwrap();
      notify({ type: "success", message: "BOM deleted successfully." });
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Deletions failed. Please try again.",
      });
    }

    setSelectedOrder([]);
    setIsDeleting(false);
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "orderless-entry",
        label: "Orderless Manufacturing Entry",
        onClick: () => setIsOrderlessEntryOpen(true),
        disabled: isDisableOrderlessEntry,
        title: isDisableOrderlessEntry
          ? "Select a single active standard BOM for orderless entry."
          : "",
      },
      {
        key: "delete",
        label: "Delete",
        onClick: () => setIsDeleting(true),
        disabled: selectedOrderId.length !== 1 || isDeleteLoading,
        title: selectedOrderId.length === 1 ? "" : "Select a single BOM to delete.",
        isLoading: isDeleteLoading,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDisableOrderlessEntry, selectedOrderId.length, isDeleteLoading],
  );

  return (
    <>
      <li className={"tableButton activeState"}>
        <button onClick={() => setIsCreating(true)}>Create</button>
      </li>
      <li
        className={`tableButton ${selectedOrderId.length === 1 ? "activeState" : "disableState"}`}
      >
        <button
          onClick={handleUpdate}
          disabled={selectedOrderId.length !== 1}
          title={selectedOrderId.length === 1 ? "" : "Select a single BOM to update."}
        >
          Update
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {isCreating && <SelectBomTypeModal closeModal={() => setIsCreating(false)} />}

      {isDeleting && (
        <ConfirmationModal
          titleOfModal="Delete BOM"
          btnName="Delete"
          closeModal={() => setIsDeleting(false)}
          handleModalWork={handleDelete}
          description="Are you sure you want to delete selected BOM?"
          labelForConfirmCheck="Confirm deletion"
          isLoading={isDeleteLoading}
        />
      )}

      {isOrderlessEntryOpen && (
        <OrderlessManufacturingEntryModal
          bomId={selectedOrderId[0]}
          closeModal={() => setIsOrderlessEntryOpen(false)}
          onSuccess={() => setSelectedOrder([])}
        />
      )}
    </>
  );
};

const SelectBomTypeModal = ({ closeModal }) => {
  const navigate = useNavigate();

  const BOM_TYPES = [
    {
      title: "Standard",
      description:
        "Define the materials and production steps required to manufacture your own products.",
      typeId: "standard",
    },
    {
      title: "Co-Manufacturing",
      description:
        "Manage outsourced production by tracking components, services, and manufacturing costs.",
      typeId: "co-manufacturing",
    },
  ];

  const handleItemClick = typeId => {
    navigate("/bom/create", { state: { type_id: typeId } });
    closeModal();
  };

  const modalContent = (
    <ModalOverlay aria-modal="true" role="dialog">
      <ModalT1 $minWidth={590} $maxWidth={760} onClick={e => e.stopPropagation()}>
        <PortalHeader title="Select BOM Type" onClose={closeModal} />
        <StyledBomTypeContainer>
          {BOM_TYPES.map((item, index) => (
            <StyledBomTypeItem key={index} onClick={() => handleItemClick(item.typeId)}>
              <StyledBomTextContent>
                <StyledBomTitle className="promo-title">{item.title}</StyledBomTitle>
                <StyledBomDescription className="promo-desc">
                  {item.description}
                </StyledBomDescription>
              </StyledBomTextContent>
              <Arrow direction="right" width="8px" />
            </StyledBomTypeItem>
          ))}
        </StyledBomTypeContainer>
      </ModalT1>
    </ModalOverlay>
  );

  return createPortal(modalContent, rootModals);
};

export const BomActionWithCheck = componentWithCondition(BomAction);
